import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { Channel, LeadFlag, LeadRow, Officer } from '../api/types'
import { AddContactModal } from '../components/AddContactModal'
import { Icon } from '../components/Icon'
import { Modal } from '../components/Modal'
import { Empty, ErrorState, Loading } from '../components/States'
import { useToast } from '../components/Toast'
import { equityOf } from '../lib/equity'
import { initials, titleCase, usdShort } from '../lib/format'
import { stageColor, tint } from '../lib/tint'
import { useAsync } from '../lib/useAsync'

// The order a loan officer reaches for a channel: cheapest and least gated
// first. Mail needs no consent at all; WhatsApp needs the most.
export const CHANNELS: { key: Channel; icon: string; label: string }[] = [
  { key: 'mail', icon: 'mail', label: 'Direct mail' },
  { key: 'call', icon: 'call', label: 'Phone' },
  { key: 'sms', icon: 'sms', label: 'SMS' },
  { key: 'whatsapp', icon: 'chat', label: 'WhatsApp' },
  { key: 'email', icon: 'alternate_email', label: 'Email' },
]

const FLAGS: { key: LeadFlag; label: string; help: string }[] = [
  { key: 'needs_touch', label: 'Needs first touch', help: 'Nobody has sent them anything yet — no mail, call, text or email.' },
  { key: 'dnc', label: 'DNC flagged', help: 'A phone on the Do-Not-Call registry. Mail and email are still open.' },
]

const GRID = '32px 50px 2.4fr 0.8fr 0.6fr 168px 0.8fr 0.75fr'
const PAGE = 250

/** The Leads tab: the campaign book as the lead platform presents it — scored,
 *  filtered by campaign, and saying for every row what may actually be done with
 *  that person. Where someone *stands* is a different question, asked on the
 *  Status board under Contacts; this list is about who to work next. */
export function Leads() {
  const [campaign, setCampaign] = useState('')
  const [flag, setFlag] = useState<LeadFlag | ''>('')
  const [rows, setRows] = useState(PAGE)
  const [picked, setPicked] = useState<Set<number>>(new Set())
  const [adding, setAdding] = useState(false)
  const [assigning, setAssigning] = useState(false)
  const nav = useNavigate()

  const { data, error, loading, reload } = useAsync(
    () => api.leads(campaign || undefined, flag || undefined),
    [campaign, flag],
  )

  const leads = data?.leads ?? []
  const selected = useMemo(() => leads.filter((l) => picked.has(l.id)), [leads, picked])

  // What the selection can actually be reached by. Counted from the same gate the
  // send buttons use, so the number here and the refusal there never disagree.
  const reach = useMemo(() => {
    const out: Partial<Record<Channel, number>> = {}
    for (const { key } of CHANNELS) out[key] = selected.filter((l) => l.channels?.[key]?.allowed).length
    return out
  }, [selected])

  const toggle = (id: number) => setPicked((s) => {
    const next = new Set(s)
    if (!next.delete(id)) next.add(id)
    return next
  })

  const allShown = leads.slice(0, rows)
  const allPicked = allShown.length > 0 && allShown.every((l) => picked.has(l.id))

  function filterBy(next: { campaign?: string; flag?: LeadFlag | '' }) {
    setPicked(new Set())
    setRows(PAGE)
    if (next.campaign !== undefined) setCampaign(next.campaign)
    if (next.flag !== undefined) setFlag(next.flag)
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="h1">Leads</h1>
          <p className="subline">
            {data
              ? data.total === data.book
                ? `${data.book} leads in your book, strongest first.`
                : `${data.total} of ${data.book} leads in your book.`
              : 'The campaign book, strongest first.'}
          </p>
        </div>
        <button className="btn primary" onClick={() => setAdding(true)}>
          <Icon name="person_add" size={17} /> Add lead
        </button>
      </div>

      {/* Campaign first, because that is the question an officer asks first:
          which mailing am I working. The two flags narrow whichever campaign
          is on screen rather than replacing it. */}
      <div className="smartlist" style={{ alignItems: 'center' }}>
        <button className={`slchip${campaign === '' ? ' active' : ''}`}
          onClick={() => filterBy({ campaign: '' })}>
          All campaigns
        </button>
        {(data?.campaigns ?? []).map((c) => (
          <button key={c.campaign} className={`slchip${campaign === c.campaign ? ' active' : ''}`}
            onClick={() => filterBy({ campaign: c.campaign })}>
            {c.campaign} <span className="n">{c.leads}</span>
          </button>
        ))}
        <span className="spacer" />
        {FLAGS.map((f) => (
          <button key={f.key} title={f.help}
            className={`slchip${flag === f.key ? ' active' : ''}`}
            onClick={() => filterBy({ flag: flag === f.key ? '' : f.key })}>
            {f.label}
          </button>
        ))}
      </div>

      {picked.size ? (
        <div className="bulkbar">
          <strong>{picked.size} selected</strong>
          <span className="bulk-label">Can be reached by:</span>
          {CHANNELS.filter((c) => c.key !== 'whatsapp' && c.key !== 'call').map((c) => (
            <span key={c.key} className="bulk-reach"
              title={`${reach[c.key]} of the ${picked.size} selected can be reached by ${c.label.toLowerCase()} today.`}>
              <Icon name={c.icon} size={15} /> {c.label} <b>{reach[c.key]}</b>
            </span>
          ))}
          <span className="spacer" />
          <button className="btn sm" onClick={() => setAssigning(true)}>Assign to officer</button>
          <button className="btn sm" onClick={() => setPicked(new Set())}>Clear</button>
        </div>
      ) : null}

      <div className="card">
        {loading && !data ? (
          <Loading label="Loading the lead book…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : leads.length === 0 ? (
          <Empty title={flag || campaign ? 'No leads match these filters' : 'No campaign leads yet'}>
            {flag || campaign
              ? <button className="btn sm" onClick={() => filterBy({ campaign: '', flag: '' })}>Clear filters</button>
              : 'Leads arrive here when a campaign is pulled and mailed.'}
          </Empty>
        ) : (
          <>
            <div className="gt-head" style={{ gridTemplateColumns: GRID }}>
              <span>
                <input type="checkbox" aria-label="Select every lead shown" checked={allPicked}
                  onChange={() => setPicked(allPicked ? new Set() : new Set(allShown.map((l) => l.id)))} />
              </span>
              <span>Rank</span>
              <span>Owner / property</span>
              <span>Campaign</span>
              <span className="right">Equity</span>
              <span>Contactable channels</span>
              <span>Status</span>
              <span>Owner</span>
            </div>
            {allShown.map((l) => (
              <LeadRowView key={l.id} lead={l} picked={picked.has(l.id)}
                onToggle={() => toggle(l.id)} onOpen={() => nav(`/leads/${l.id}`)} />
            ))}
            <div className="gt-foot">
              <span>Showing {Math.min(rows, leads.length)} of {leads.length}</span>
              {leads.length > rows ? (
                <button className="btn sm" onClick={() => setRows((n) => n + PAGE)}>Show more</button>
              ) : null}
            </div>
          </>
        )}
      </div>

      {assigning ? (
        <AssignModal ids={[...picked]} onClose={() => setAssigning(false)}
          onDone={() => { setPicked(new Set()); reload() }} />
      ) : null}
      <AddContactModal open={adding} onClose={() => { setAdding(false); reload() }} />
    </div>
  )
}

function LeadRowView({ lead, picked, onToggle, onOpen }: {
  lead: LeadRow; picked: boolean; onToggle: () => void; onOpen: () => void
}) {
  const place = [lead.address, lead.city, lead.state].filter(Boolean).join(', ')
  return (
    // A row with a checkbox in it cannot be a <button>, so it is a div that
    // behaves like one: Enter and Space open the lead, as the old row did.
    <div className="gt-row" role="button" tabIndex={0} style={{ gridTemplateColumns: GRID }}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() } }}>
      <span onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={picked} onChange={onToggle}
          aria-label={`Select ${lead.full_name ?? 'lead'}`} />
      </span>
      <span><RankPill rank={lead.rank} /></span>
      <span style={{ minWidth: 0, display: 'flex', gap: 9, alignItems: 'center' }}>
        <Icon name="home" size={16} style={{ color: 'var(--ink-4)', flex: 'none' }} />
        <span style={{ minWidth: 0 }}>
          <span className="gt-name">{lead.full_name ?? '—'}</span>
          <span className="gt-sub">{place || '—'}</span>
        </span>
      </span>
      <span>{lead.campaign
        ? <span className="camp-tag">{lead.campaign}</span>
        : <span style={{ color: 'var(--ink-4)' }}>—</span>}</span>
      <span className="right mono">{usdShort(equityOf(lead))}</span>
      <span><ChannelChips lead={lead} /></span>
      <span><StatusChip status={lead.status} /></span>
      <span style={{ display: 'flex', gap: 7, alignItems: 'center', minWidth: 0 }}>
        {lead.owner ? (
          <>
            <span className="gt-av" style={{ ...tint(lead.owner), width: 24, height: 24, fontSize: 10 }}>
              {initials(lead.owner)}
            </span>
            <span className="gt-sub">{lead.owner.split(' ')[0]}</span>
          </>
        ) : <span className="gt-sub">Unassigned</span>}
      </span>
    </div>
  )
}

/** The score, as the lead platform shows it: filled when it is worth stopping
 *  on, outlined when it is just in the list. 125 is where the campaigns' own
 *  "hot" cut sits. */
export function RankPill({ rank }: { rank: number | null }) {
  if (rank === null) return <span style={{ color: 'var(--ink-4)' }}>—</span>
  return <span className={`rankpill${rank >= 125 ? ' hot' : ''}`} title="Lead score">{Math.round(rank)}</span>
}

export function StatusChip({ status }: { status: string }) {
  const terminal = status === 'dead' || status === 'funded'
  return (
    <span className="status-chip">
      <span className="dot" style={{ background: stageColor(status, terminal) }} />
      {titleCase(status)}
    </span>
  )
}

/** Five squares saying what may be done with this person today, green for yes
 *  and red for no. The answer comes from the server's compliance gate, not from
 *  looking at whether a phone number exists — a number on the DNC registry is a
 *  number you have and may not call. */
export function ChannelChips({ lead }: { lead: Pick<LeadRow, 'channels' | 'full_name'> }) {
  return (
    <span className="chrow">
      {CHANNELS.map((c) => {
        const state = lead.channels?.[c.key]
        const ok = !!state?.allowed
        return (
          <span key={c.key} className={`chbox ${ok ? 'ok' : 'no'}`}
            title={ok ? `${c.label}: ready` : `${c.label}: ${state?.reason || 'not available'}`}>
            <Icon name={c.icon} size={14} />
          </span>
        )
      })}
    </span>
  )
}

/** Hand the picked rows to one officer. An officer can take leads for
 *  themselves; only an admin sees everyone in the list. */
function AssignModal({ ids, onClose, onDone }: { ids: number[]; onClose: () => void; onDone: () => void }) {
  const { data } = useAsync(() => api.officers(), [])
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  // /api/officers already returns only active officers.
  const officers = data?.officers ?? []

  async function assign(o: Officer) {
    setBusy(true)
    try {
      const r = await api.assignSelected(ids, o.id)
      toast(r.assigned
        ? `${r.assigned} ${r.assigned === 1 ? 'lead' : 'leads'} → ${r.officer}`
        : `${r.officer} already had all ${ids.length}.`, { tone: 'positive' })
      onDone()
      onClose()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
      setBusy(false)
    }
  }

  return (
    <Modal open width={460} onClose={onClose} title={`Assign ${ids.length} ${ids.length === 1 ? 'lead' : 'leads'}`}
      subtitle="They move straight away, and the handover is logged on each lead.">
      <div className="stack">
        {officers.length === 0 ? <p style={{ margin: 0 }}>No active loan officers to assign to.</p> : null}
        {officers.map((o: Officer) => (
          <button key={o.id} className="card wf-choice" disabled={busy} onClick={() => assign(o)}>
            <span className="gt-av" style={tint(o.name)}>{initials(o.name)}</span>
            <span style={{ minWidth: 0 }}>
              <span style={{ fontWeight: 600, display: 'block' }}>{o.name}</span>
              <span className="quiet" style={{ fontSize: 12 }}>{o.email}</span>
            </span>
          </button>
        ))}
      </div>
    </Modal>
  )
}
